import { Fragment, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@shared/components/ui/cn';

import { precedingUserFiles } from '../proposal-files';
import type {
  ChatSystemNote,
  Conversation,
  InteractionAnswer,
  Message,
  MessageFeedback,
  ProposalDecision,
} from '../types';

import { MessageRow, ThinkingDots } from './MessageRow';
import { SystemNote } from './SystemNote';
import { ChatConversationProvider } from './chat-context';

interface ChatThreadProps {
  conversation: Conversation;
  onFeedback: (id: string, feedback: MessageFeedback) => void;
  onRegenerate: (id: string) => void;
  onAnswerInteraction: (answer: InteractionAnswer) => void;
  onDecideProposal: (decision: ProposalDecision) => void;
  /** A reply is being written: no reply is regenerated meanwhile. */
  busy?: boolean;
  /** The reply being written is one this tab does not read: one line says so. */
  following?: boolean;
  /** Stop was pressed and the reply has not ended. */
  stopping?: boolean;
  /**
   * The last turn the session's stored compaction folded (the cut), null for none: the divider
   * marks where the model's summary begins.
   */
  compactedThroughTurn?: number | null;
  onRemoveQueued?: (messageId: string) => void;
  onSendQueuedNow?: () => void;
  onAnswerInternal?: (id: string) => void;
  onAskInNewChat?: () => void;
  className?: string;
}

function notesByMessage(notes: ChatSystemNote[] | undefined): Map<string | null, ChatSystemNote[]> {
  const map = new Map<string | null, ChatSystemNote[]>();
  for (const note of notes ?? []) {
    const list = map.get(note.afterMessageId) ?? [];
    list.push(note);
    map.set(note.afterMessageId, list);
  }
  return map;
}

/**
 * Whether regenerating this reply would resend its turn without the files the
 * user attached. A regenerate passes the turn's files on for the data policy
 * only, without their refs or bytes, so it attaches none of them again: it
 * counts on the session holding what the first send attached. An error reply
 * to a turn with files (a refused file, most often) offers neither Regenerate
 * nor the in-house retry, since the AI CIO never received that file and the
 * bubble would still show it.
 */
function resendsWithoutFiles(messages: Message[], index: number): boolean {
  if (messages[index]?.status !== 'error') return false;
  return Boolean(precedingUserFiles(messages, index)?.length);
}

function repliesToEcho(messages: Message[], index: number): boolean {
  for (let i = index - 1; i >= 0; i--) {
    const prior = messages[i];
    if (prior?.role === 'user') return prior.echoOf !== undefined;
  }
  return false;
}

function answeredByNext(messages: Message[], index: number): boolean {
  const next = messages[index + 1];
  return (
    messages[index]?.role === 'assistant' && next?.role === 'user' && next.echoOf !== undefined
  );
}

/**
 * Where the divider goes: after the last row whose turn id is at or below the cut, so a row out
 * of turn order (a regenerated earlier reply holds its new turn's id) does not move it; -1 for
 * no divider (no cut, or no row at or below it).
 */
function lastFolded(rows: Message[], cut: number | null): number {
  if (cut === null) return -1;
  for (let i = rows.length - 1; i >= 0; i--) {
    const turnId = rows[i]?.turnId;
    if (turnId !== undefined && turnId <= cut) return i;
  }
  return -1;
}

/**
 * The row that marks where the model's summary begins: the rows above it are summarised for
 * the model, and shown whole. Not a message: a caption between two rules.
 */
function SummaryDivider() {
  const { t } = useTranslation('ai-cio');
  return (
    <div className="my-2 flex items-center gap-2 text-xs text-muted-foreground">
      <span aria-hidden="true" className="h-px flex-1 bg-border" />
      <span className="min-w-0 text-center">
        {t('message.dividerSummarised', 'Earlier messages are summarised for the model')}
      </span>
      <span aria-hidden="true" className="h-px flex-1 bg-border" />
    </div>
  );
}

/** Scrollable message list that auto-scrolls to the latest content. */
export function ChatThread({
  conversation,
  onFeedback,
  onRegenerate,
  onAnswerInteraction,
  onDecideProposal,
  busy = false,
  following = false,
  stopping = false,
  compactedThroughTurn = null,
  onRemoveQueued,
  onSendQueuedNow,
  onAnswerInternal,
  onAskInNewChat,
  className,
}: ChatThreadProps) {
  const { t } = useTranslation('ai-cio');
  const bottomRef = useRef<HTMLDivElement>(null);

  const lastMessage = conversation.messages.at(-1);
  const noteCount = conversation.systemNotes?.length ?? 0;
  // Auto-scroll on new message and as the streaming reply grows.
  // (scrollIntoView is absent in jsdom; guard so tests/SSR don't throw.)
  useEffect(() => {
    const node = bottomRef.current;
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [
    conversation.messages.length,
    lastMessage?.content,
    lastMessage?.blocks?.length,
    lastMessage?.pendingFiles?.length,
    noteCount,
  ]);

  const notes = notesByMessage(conversation.systemNotes);

  // Queued messages wait last, after the reply being written.
  const firstQueued = conversation.messages.findIndex((m) => m.status === 'queued');
  const thread =
    firstQueued === -1 ? conversation.messages : conversation.messages.slice(0, firstQueued);
  const queued = firstQueued === -1 ? [] : conversation.messages.slice(firstQueued);
  const divider = lastFolded(thread, compactedThroughTurn);
  const resendable = (index: number) =>
    !busy && !resendsWithoutFiles(thread, index) && !repliesToEcho(thread, index);
  const threadIds = new Set(thread.map((m) => m.id));
  const orphanNotes = (conversation.systemNotes ?? []).filter(
    (note) => note.afterMessageId === null || !threadIds.has(note.afterMessageId),
  );

  return (
    <div className={cn('overflow-y-auto', className)}>
      <div className="flex flex-col gap-5 pe-4">
        <ChatConversationProvider conversationId={conversation.id}>
          {thread.map((message, index) => (
            <Fragment key={message.id}>
              <MessageRow
                message={message}
                conversationId={conversation.id}
                relatedFiles={
                  message.role === 'assistant' ? precedingUserFiles(thread, index) : undefined
                }
                onFeedback={onFeedback}
                onRegenerate={
                  !resendable(index)
                    ? undefined
                    : () => {
                        onRegenerate(message.id);
                      }
                }
                answered={answeredByNext(thread, index)}
                onAnswerInteraction={onAnswerInteraction}
                onDecideProposal={onDecideProposal}
                onAnswerInternal={
                  onAnswerInternal && resendable(index)
                    ? () => {
                        onAnswerInternal(message.id);
                      }
                    : undefined
                }
                onAskInNewChat={onAskInNewChat}
              />
              {index === divider && <SummaryDivider />}
              {notes.get(message.id)?.map((note) => (
                <SystemNote key={note.id} note={note} />
              ))}
            </Fragment>
          ))}
          {orphanNotes.map((note) => (
            <SystemNote key={note.id} note={note} />
          ))}
          {following && (
            <ThinkingDots
              label={t('message.stillAnswering', 'Still answering')}
              status={
                stopping
                  ? t('composer.stopping', 'Stopping…')
                  : t('message.stillAnswering', 'Still answering')
              }
            />
          )}
          {queued.map((message) => (
            <MessageRow
              key={message.id}
              message={message}
              onFeedback={onFeedback}
              onAnswerInteraction={onAnswerInteraction}
              onDecideProposal={onDecideProposal}
              onRemoveQueued={onRemoveQueued}
              onSendNow={onSendQueuedNow}
            />
          ))}
        </ChatConversationProvider>
        <div ref={bottomRef} />
      </div>
    </div>
  );
}
